import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { initTheme } from "@/lib/theme";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import DriverApplication from "@/pages/DriverApplication";
import AirportPickup from "@/pages/AirportPickup";
import HousingGuide from "@/pages/HousingGuide";
import JobsSection from "@/pages/JobsSection";
import EducationSection from "@/pages/EducationSection";
import TourismGuide from "@/pages/TourismGuide";
import ChatRooms from "@/pages/ChatRooms";
import Community from "@/pages/Community";
import CommunityGroup from "@/pages/CommunityGroup";
import Messages from "@/pages/Messages";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import ArabSanadTV from "@/pages/ArabSanadTV";
import AIAssistant from "@/pages/AIAssistant";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Subscriptions from "@/pages/Subscriptions";
import PropertyMarketplace from "@/pages/PropertyMarketplace";
import PropertyDetail from "@/pages/PropertyDetail";
import PostProperty from "@/pages/PostProperty";
import MyProperties from "@/pages/MyProperties";
import VehicleMarketplace from "@/pages/VehicleMarketplace";
import VehicleDetail from "@/pages/VehicleDetail";
import PostVehicle from "@/pages/PostVehicle";
import MyVehicles from "@/pages/MyVehicles";
import ChatInbox from "@/pages/ChatInbox";
import ChatConversation from "@/pages/ChatConversation";
import SupportChat from "@/pages/SupportChat";
import AdminSupportInbox from "@/pages/AdminSupportInbox";
import TourismDiscovery from "@/pages/TourismDiscovery";
import AttractionDetail from "@/pages/AttractionDetail";
import Favorites from "@/pages/Favorites";
import JobsSearch from "@/pages/JobsSearch";
import SavedJobs from "@/pages/SavedJobs";
import CVCenter from "@/pages/CVCenter";
import ResumeBuilder from "@/pages/ResumeBuilder";
import ResumeWizard from "@/pages/ResumeWizard";
import CVPremium from "@/pages/CVPremium";
import CVTools from "@/pages/CVTools";
import StoresMarketplace from "@/pages/StoresMarketplace";
import GroceryComingSoon from "@/pages/GroceryComingSoon";
import StoreDetail from "@/pages/StoreDetail";
import MyStorePortal from "@/pages/MyStorePortal";
import ShoppingListCreate from "@/pages/ShoppingListCreate";
import MyOrders from "@/pages/MyOrders";
import DriverGroceryDashboard from "@/pages/DriverGroceryDashboard";
import ServicesDirectory from "@/pages/ServicesDirectory";
import ProviderDetail from "@/pages/ProviderDetail";
import MyProvider from "@/pages/MyProvider";
import GuideHome from "@/pages/GuideHome";
import GuideArticle from "@/pages/GuideArticle";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import RefundPolicy from "@/pages/RefundPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Disclaimer from "@/pages/Disclaimer";
import DeleteAccount from "@/pages/DeleteAccount";
import AboutUs from "@/pages/AboutUs";
import ContactUs from "@/pages/ContactUs";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Language Context
const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Language Provider
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem("lang") || "ar");

  const toggleLanguage = () => {
    const newLang = language === "ar" ? "en" : "ar";
    setLanguage(newLang);
    localStorage.setItem("lang", newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
  };

  useEffect(() => {
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language]);

  const t = (ar, en) => language === "ar" ? ar : en;

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const savedToken = localStorage.getItem("token");
    if (!savedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` },
        withCredentials: true
      });
      setUser(response.data);
      setToken(savedToken);
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem("token", accessToken);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !["admin", "owner", "support", "viewer", "video_contributor"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Google Analytics SPA page_view: emit a virtual pageview on every route change.
  // The gtag script is loaded from index.html. We re-send `config` so GA registers
  // the new path. Guarded so the app works fine in environments where gtag is absent.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const gaId = process.env.REACT_APP_GA_ID;
    if (!gaId) return;
    try {
      window.gtag("config", gaId, {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    } catch { /* noop */ }
  }, [location.pathname, location.search]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/driver-application" element={<ProtectedRoute><DriverApplication /></ProtectedRoute>} />
      <Route path="/airport-pickup" element={<ProtectedRoute><AirportPickup /></ProtectedRoute>} />

      {/* Housing Marketplace */}
      <Route path="/housing" element={<ProtectedRoute><PropertyMarketplace /></ProtectedRoute>} />
      <Route path="/housing/post" element={<ProtectedRoute><PostProperty /></ProtectedRoute>} />
      <Route path="/housing/mine" element={<ProtectedRoute><MyProperties /></ProtectedRoute>} />
      <Route path="/housing/guide" element={<ProtectedRoute><HousingGuide /></ProtectedRoute>} />
      <Route path="/housing/:id" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />

      {/* Vehicle Marketplace */}
      <Route path="/vehicles" element={<ProtectedRoute><VehicleMarketplace /></ProtectedRoute>} />
      <Route path="/vehicles/post" element={<ProtectedRoute><PostVehicle /></ProtectedRoute>} />
      <Route path="/vehicles/mine" element={<ProtectedRoute><MyVehicles /></ProtectedRoute>} />
      <Route path="/vehicles/:id" element={<ProtectedRoute><VehicleDetail /></ProtectedRoute>} />

      {/* Chat */}
      <Route path="/chat/inbox" element={<ProtectedRoute><ChatInbox /></ProtectedRoute>} />
      <Route path="/chat/conversation/:id" element={<ProtectedRoute><ChatConversation /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportChat /></ProtectedRoute>} />
      <Route path="/admin/support" element={<ProtectedRoute><AdminSupportInbox /></ProtectedRoute>} />

      {/* Tourism Discovery */}
      <Route path="/discover" element={<ProtectedRoute><TourismDiscovery /></ProtectedRoute>} />
      <Route path="/discover/:id" element={<ProtectedRoute><AttractionDetail /></ProtectedRoute>} />
      <Route path="/tourism" element={<ProtectedRoute><TourismDiscovery /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />

      {/* Smart Jobs Search */}
      <Route path="/jobs" element={<ProtectedRoute><JobsSearch /></ProtectedRoute>} />
      <Route path="/jobs/saved" element={<ProtectedRoute><SavedJobs /></ProtectedRoute>} />

      {/* CV Center — Resume Builder + Tools */}
      <Route path="/cv" element={<ProtectedRoute><CVCenter /></ProtectedRoute>} />
      <Route path="/cv/wizard" element={<ProtectedRoute><ResumeWizard /></ProtectedRoute>} />
      <Route path="/cv/premium" element={<ProtectedRoute><CVPremium /></ProtectedRoute>} />
      <Route path="/cv/edit/:id" element={<ProtectedRoute><ResumeBuilder /></ProtectedRoute>} />
      <Route path="/cv/tools/:tool" element={<ProtectedRoute><CVTools /></ProtectedRoute>} />

      {/* Grocery Marketplace */}
      <Route path="/grocery" element={<ProtectedRoute><StoresMarketplace /></ProtectedRoute>} />
      <Route path="/grocery/coming-soon" element={<ProtectedRoute><GroceryComingSoon /></ProtectedRoute>} />
      <Route path="/grocery/store/:id" element={<ProtectedRoute><StoreDetail /></ProtectedRoute>} />
      <Route path="/grocery/mystore" element={<ProtectedRoute><MyStorePortal /></ProtectedRoute>} />
      <Route path="/grocery/list" element={<ProtectedRoute><ShoppingListCreate /></ProtectedRoute>} />
      <Route path="/grocery/orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
      <Route path="/grocery/driver" element={<ProtectedRoute><DriverGroceryDashboard /></ProtectedRoute>} />

      {/* Social Services Directory */}
      <Route path="/services" element={<ProtectedRoute><ServicesDirectory /></ProtectedRoute>} />
      <Route path="/services/mine" element={<ProtectedRoute><MyProvider /></ProtectedRoute>} />
      <Route path="/services/:id" element={<ProtectedRoute><ProviderDetail /></ProtectedRoute>} />

      {/* Guide / Knowledge Base */}
      <Route path="/guide" element={<ProtectedRoute><GuideHome /></ProtectedRoute>} />
      <Route path="/guide/article/:slug" element={<ProtectedRoute><GuideArticle /></ProtectedRoute>} />

      <Route path="/jobs-legacy" element={<ProtectedRoute><JobsSection /></ProtectedRoute>} />
      <Route path="/education" element={<ProtectedRoute><EducationSection /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatRooms /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
      <Route path="/community/:slug" element={<ProtectedRoute><CommunityGroup /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/messages/:tid" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/tv" element={<ProtectedRoute><ArabSanadTV /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
      <Route path="/subscriptions" element={<ProtectedRoute><Subscriptions /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />

      {/* Legacy Admin Dashboard (old 10-tab marketplace admin) — kept under /admin-legacy */}
      <Route path="/admin-legacy" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />

      {/* Public Legal & Info Pages (no auth required) */}
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/refunds" element={<RefundPolicy />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/delete-account" element={<DeleteAccount />} />

      {/* Password reset flow */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => { initTheme(); }, []);
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors theme="light" />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
